import { useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import { WELLNESS_QUESTIONNAIRE, WellnessMetricKey } from './questionnaire';
import { parseScaleLabels } from './wellnessUtils';

interface MetricTooltipProps {
  metricKey: WellnessMetricKey;
  className?: string;
}

export function MetricTooltip({ metricKey, className = '' }: MetricTooltipProps) {
  const questionnaire = useMemo(() => WELLNESS_QUESTIONNAIRE[metricKey], [metricKey]);
  const scaleLabels = useMemo(() => parseScaleLabels(questionnaire.scale), [questionnaire.scale]);

  return (
    <div className={`group relative ${className}`}>
      <HelpCircle className="h-4 w-4 cursor-help text-[var(--anl-text-muted)] transition-colors hover:text-[var(--anl-text)]" />

      <div className="pointer-events-none absolute bottom-full right-0 z-50 mb-2 w-80 rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] text-[var(--anl-text)] opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
        <div className="space-y-4 p-4">
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--anl-text-muted)]">
              Question Asked
            </div>
            <p className="text-sm leading-relaxed">{questionnaire.question}</p>
            <p className="mt-2 text-xs italic text-[var(--anl-text-muted)]">
              Values shown are averages of session results
            </p>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--anl-text-muted)]">
              Value Scale
            </div>
            <div className="space-y-2">
              <div className="relative">
                <div className="h-3 w-full rounded-full bg-gradient-to-r from-red-700 via-yellow-600 to-green-600"></div>
                <div className="mt-1 flex justify-between text-xs font-medium text-[var(--anl-text-muted)]">
                  <span>{scaleLabels.low}</span>
                  <span>{scaleLabels.mid}</span>
                  <span>{scaleLabels.high}</span>
                </div>
                <div className="mt-0.5 flex justify-between text-xs text-[var(--anl-text-muted)]">
                  <span>0.0</span>
                  <span>0.5</span>
                  <span>1.0</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className="absolute right-3 top-full h-0 w-0 border-l-4 border-r-4 border-t-4 border-transparent"
          style={{ borderTopColor: 'var(--anl-surface-elev)' }}
        ></div>
      </div>
    </div>
  );
}
